import {
  ArrowBigRightDash,
  Box,
  Lock,
  Search,
  Settings,
  Sparkles,
} from "lucide-react";
import { GlowingEffect } from "../ui/glowing-effect";
import { useId } from "react";

export function GlowingEffectDemo() {
  return (
    <ul className="grid container lg:max-w-5xl grid-cols-1 grid-rows-none mt-12 lg:mt-20 gap-4 md:grid-cols-12 md:grid-rows-2 lg:gap-4 xl:max-h-[34rem] xl:grid-rows-2">
      <GridItem
        area="md:[grid-area:1/1/2/7] xl:[grid-area:1/1/2/5"
        icon={<Box className="h-4 w-4 text-gold" />}
        more={<ArrowBigRightDash className="h-6 w-6 text-gray-600" />}
        title="Driving Growth Through Strategic Mergers & Acquisitions"
        description="We guide businesses from opportunity discovery to integration, delivering impactful M&A strategies that fuel long-term success."
      />
      <GridItem
        area="md:[grid-area:1/7/2/13] xl:[grid-area:2/1/3/5"
        icon={<Settings className="h-4 w-4 text-gold" />}
        more={<ArrowBigRightDash className="h-6 w-6 text-gray-600" />}
        title="Empowering Growth Through Strategic Finance and Business Advisory"
        description="From insightful financial guidance to operational optimization, we help 
        businesses achieve clarity, resilience, and long-term success."
      />
      <GridItem
        area="md:[grid-area:2/1/3/7] xl:[grid-area:1/5/3/8"
        icon={<Lock className="h-4 w-4 text-gold" />}
        more={<ArrowBigRightDash className="h-6 w-6 text-gray-600" />}
        title="Strategic Real Estate Advisory for Smart Investments"
        description="We combine market expertise with customized advisory to unlock the full 
        potential of your real estate portfolio."
      />
      <GridItem
        area="md:[grid-area:2/7/3/13] xl:[grid-area:1/8/2/13"
        icon={<Sparkles className="h-4 w-4 text-gold" />}
        more={<ArrowBigRightDash className="h-6 w-6 text-gray-600" />}
        title="Tailored Tax Solutions for Confident Growth"
        description="From compliance to strategic planning, our tax consulting services help 
        you make smarter decisions that fuel sustainable success."
      />
    </ul>
  );
}

const GridItem = ({ area, icon, title, more, description }) => {
  return (
    <li
      className={`min-h-[14rem] list-none cursor-pointer overflow-hidden bg-black ${area}`}
    >
      <div className="relative h-full rounded-2xl border p-2 md:rounded-3xl md:p-3">
        <GlowingEffect
          spread={40}
          glow={true}
          disabled={false}
          proximity={64}
          inactiveZone={0.01}
        />
        <Grid size={20} />
        <div className="border-0.75 relative flex h-full flex-col justify-between gap-6 overflow-hidden rounded-xl p-6 md:p-6 shadow-[0px_0px_27px_0px_#2D2D2D]">
          <div className="relative flex flex-1 flex-col justify-between gap-3">
            <div className="flex justify-between items-center">
              <div className="w-fit rounded-lg border border-gray-600 p-2">
                {icon}
              </div>
              <div>{more}</div>
            </div>
            <div className="space-y-3">
              <h3 className="-tracking-4 pt-0.5 font-sans text-xl/[1.375rem] font-semibold text-balance md:text-xl/[1.875rem] text-white">
                {title}
              </h3>
              <h2 className="font-sans text-sm/[1.125rem] md:text-base/[1.375rem] text-neutral-400 [&_b]:md:font-semibold [&_strong]:md:font-semibold">
                {description}
              </h2>
            </div>
          </div>
        </div>
      </div>
    </li>
  );
};

// grid background
export const Grid = ({ pattern, size }) => {
  const p = pattern ?? [
    [Math.floor(Math.random() * 4) + 7, Math.floor(Math.random() * 6) + 1],
    [Math.floor(Math.random() * 4) + 7, Math.floor(Math.random() * 6) + 1],
    [Math.floor(Math.random() * 4) + 7, Math.floor(Math.random() * 6) + 1],
    [Math.floor(Math.random() * 4) + 7, Math.floor(Math.random() * 6) + 1],
    [Math.floor(Math.random() * 4) + 7, Math.floor(Math.random() * 6) + 1],
  ];
  return (
    <div className="pointer-events-none absolute left-1/2 top-0  -ml-20 -mt-2 h-full w-full [mask-image:linear-gradient(white,transparent)]">
      <div className="absolute inset-0 bg-gradient-to-r  [mask-image:radial-gradient(farthest-side_at_top,white,transparent)] from-zinc-900/30  to-zinc-900/30 opacity-100">
        <GridPattern
          width={size ?? 20}
          height={size ?? 20}
          x="-12"
          y="4"
          squares={p}
          className="absolute inset-0 h-full w-full  mix-blend-overlay fill-white/10 stroke-white/10 stroke-black/10 fill-black/10"
        />
      </div>
    </div>
  );
};

export function GridPattern({ width, height, x, y, squares, ...props }) {
  const patternId = useId();

  return (
    <svg aria-hidden="true" {...props}>
      <defs>
        <pattern
          id={patternId}
          width={width}
          height={height}
          patternUnits="userSpaceOnUse"
          x={x}
          y={y}
        >
          <path d={`M.5 ${height}V.5H${width}`} fill="none" />
        </pattern>
      </defs>
      <rect
        width="100%"
        height="100%"
        strokeWidth={0}
        fill={`url(#${patternId})`}
      />
      {squares && (
        <svg x={x} y={y} className="overflow-visible">
          {squares.map(([x, y]) => (
            <rect
              strokeWidth="0"
              key={`${x}-${y}`}
              width={width + 1}
              height={height + 1}
              x={x * width}
              y={y * height}
            />
          ))}
        </svg>
      )}
    </svg>
  );
}
